import { useState, useMemo } from 'react';
import { CalendarDays, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import '../../components/ui/datatable.css';

interface PresencaDia {
  id: string;
  dataReferencia: string;
  statusPresencaDiaria: string;
  atrasoMinutos?: number;
  entrouNaEscola: boolean;
  saiuDaEscola: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; icon: any; color: string; bg: string }> = {
  PRESENTE:     { label: 'Presente',    icon: <CheckCircle2 size={14} />, color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  AUSENTE:      { label: 'Ausente',     icon: <XCircle size={14} />,      color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  ATRASADO:     { label: 'Atrasado',    icon: <Clock size={14} />,        color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  JUSTIFICADO:  { label: 'Justificado', icon: <AlertCircle size={14} />,  color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
};

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export function FrequenciaAlunoPage() {
  const user = useAuthStore((s) => s.user);
  const alunoId = (user as any)?.alunoId || user?.id || '';
  const [ano] = useState(new Date().getFullYear());
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth());

  // Buscar presenças
  const { data: presencas = [], isLoading } = useQuery({
    queryKey: ['frequencia-aluno', alunoId, ano],
    queryFn: async () => {
      if (!alunoId) return [];
      const { data } = await api.get<PresencaDia[]>(`/presencas-diarias`, {
        params: { alunoId, dataInicio: `${ano}-01-01`, dataFim: `${ano}-12-31` },
      });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!alunoId,
  });

  // Filtrar por mês
  const presencasMes = useMemo(() => {
    return presencas.filter(p => new Date(p.dataReferencia).getMonth() === mesSelecionado);
  }, [presencas, mesSelecionado]);

  // Calcular stats
  const stats = useMemo(() => {
    const total = presencasMes.length;
    const presentes = presencasMes.filter(p => p.statusPresencaDiaria === 'PRESENTE' || p.statusPresencaDiaria === 'ATRASADO').length;
    const ausentes = presencasMes.filter(p => p.statusPresencaDiaria === 'AUSENTE').length;
    const atrasados = presencasMes.filter(p => p.statusPresencaDiaria === 'ATRASADO').length;
    const justificados = presencasMes.filter(p => p.statusPresencaDiaria === 'JUSTIFICADO').length;
    const percentual = total > 0 ? Math.round((presentes / total) * 10000) / 100 : 100;
    return { total, presentes, ausentes, atrasados, justificados, percentual };
  }, [presencasMes]);

  // Stats do ano
  const statsAno = useMemo(() => {
    const total = presencas.length;
    const presentes = presencas.filter(p => p.statusPresencaDiaria === 'PRESENTE' || p.statusPresencaDiaria === 'ATRASADO').length;
    const percentual = total > 0 ? Math.round((presentes / total) * 10000) / 100 : 100;
    return { total, presentes, percentual };
  }, [presencas]);

  return (
    <div className="admin-page">
      <div className="page-header"><div><h1>Minha Frequência</h1><p>Ano letivo {ano}</p></div></div>

      {/* KPIs do ano */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="kpi-card hover-lift" style={{ gap: 6 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Frequência Anual</span>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: statsAno.percentual >= 75 ? '#22c55e' : '#ef4444' }}>
            {statsAno.percentual.toFixed(1)}%
          </span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{statsAno.presentes}/{statsAno.total} dias</span>
        </div>
        <div className="kpi-card hover-lift" style={{ gap: 6 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Freq. {MESES[mesSelecionado]}</span>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: stats.percentual >= 75 ? '#22c55e' : '#ef4444' }}>
            {stats.percentual.toFixed(1)}%
          </span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{stats.presentes}/{stats.total} dias</span>
        </div>
        <div className="kpi-card hover-lift" style={{ gap: 6 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#ef4444' }}>Ausências</span>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ef4444' }}>{stats.ausentes}</span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>no mês</span>
        </div>
        <div className="kpi-card hover-lift" style={{ gap: 6 }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#f59e0b' }}>Atrasos</span>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f59e0b' }}>{stats.atrasados}</span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>no mês</span>
        </div>
      </div>

      {/* Filtro de mês */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
        {MESES.map((m, idx) => (
          <button
            key={m}
            onClick={() => setMesSelecionado(idx)}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600,
              border: '1px solid var(--border-color, #d1d5db)', cursor: 'pointer', transition: 'all 0.15s',
              background: mesSelecionado === idx ? '#3b82f6' : 'transparent',
              color: mesSelecionado === idx ? '#fff' : 'var(--text-muted)',
              borderColor: mesSelecionado === idx ? '#3b82f6' : undefined,
            }}
          >
            {m}
          </button>
        ))}
      </div>

      {/* Lista de presenças */}
      <div style={cardStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarDays size={16} style={{ color: '#3b82f6' }} />
          {MESES[mesSelecionado]} {ano}
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Carregando...</div>
        ) : presencasMes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Nenhum registro neste mês</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {presencasMes.sort((a, b) => new Date(b.dataReferencia).getTime() - new Date(a.dataReferencia).getTime()).map(p => {
              const cfg = STATUS_CONFIG[p.statusPresencaDiaria] || STATUS_CONFIG.PRESENTE;
              const dt = new Date(p.dataReferencia);
              return (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 8, background: cfg.bg,
                }}>
                  <span style={{ color: cfg.color }}>{cfg.icon}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-main)' }}>
                      {dt.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}
                    </span>
                  </div>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: '0.65rem',
                    fontWeight: 700, color: cfg.color, background: 'rgba(255,255,255,0.7)',
                  }}>
                    {cfg.label}
                    {p.atrasoMinutos ? ` (${p.atrasoMinutos}min)` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  borderRadius: 14, background: 'var(--bg-card, #fff)', padding: 16,
  border: '1px solid var(--border-color, #e5e7eb)',
};
